import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ApplicationService } from './application.service';
import {
  applicationSchemeToSlug,
  normalizeApplicationScheme,
} from '../constants/application-scheme';

@Injectable({
  providedIn: 'root',
})
export class ToolLinksService {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly applicationService: ApplicationService,
  ) {}

  resolveToolLink(path: string, fallback: (normalizedPath: string) => string): string {
    const normalizedPath = this.normalizePath(path);
    const applicationId = this.getCurrentApplicationIdFromRoute();

    if (applicationId) {
      if (normalizedPath === '/tools/form') {
        // return this.router.url; Commented out to avoid redirecting to the current URL, which may not be the intended behavior.
        const cached = this.applicationService.getCachedApplicationById(applicationId);
        const applicationInformation = cached?.data ?? cached;
        const rawApplicationType =
          applicationInformation?.application_type ??
          applicationInformation?.applicationType ??
          null;

        if (typeof rawApplicationType === 'string' && rawApplicationType.trim()) {
          const slug = applicationSchemeToSlug(normalizeApplicationScheme(rawApplicationType));
          return `/${slug}/application/${applicationId}`;
        }

        const dashboardUrl = this.authService.getDashboardAutoLoginUrl(
          `/dashboard?ca=1&na=${applicationId}`,
          false,
        );
        if (dashboardUrl) {
          return dashboardUrl;
        }

        // Fallback: derive prefix from current route
        const wizardPrefix = this.getCurrentWizardPrefixFromRoute(applicationId);
        return `${wizardPrefix}/application/${applicationId}`;
      }

      if (normalizedPath === '/tools/productivity') {
        const productivityUrl = this.authService.getDashboardAutoLoginUrl(
          `/applications/${applicationId}/roof-capacity`,
          false,
        );
        if (productivityUrl) {
          return productivityUrl;
        }
      }

      if (normalizedPath === '/tools/sizing') {
        return `/simulation/${applicationId}`;
      }
    }

    return fallback(normalizedPath);
  }

  private getCurrentApplicationIdFromRoute(): number | null {
    const path = this.router.url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);

    let rawApplicationId: string | undefined;
    if (segments[0] === 'simulation') {
      rawApplicationId = segments[1];
    } else if (segments[1] === 'application') {
      rawApplicationId = segments[2];
    } else if (segments[0] === 'application' || segments[0] === 'cncs-wizard') {
      rawApplicationId = segments[1];
    }

    if (!rawApplicationId) {
      return null;
    }

    const applicationId = Number(rawApplicationId);
    return Number.isInteger(applicationId) && applicationId > 0 ? applicationId : null;
  }

  private getCurrentWizardPrefixFromRoute(applicationId: number): string {
    const path = this.router.url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);

    const prefix = segments[1] === 'application' ? segments[0] : null;
    switch (prefix) {
      case 'cncs':
      case 'ssdg':
      case 'msdg':
      case 'agrivoltaic-one':
      case 'agrivoltaic-two':
        return `/${prefix}`;
      default:
        const cachedScheme = this.applicationService.getCachedApplicationSchemeById(applicationId);
        if (cachedScheme) {
          const slug = applicationSchemeToSlug(normalizeApplicationScheme(cachedScheme));
          return `/${slug}`;
        }
        return '/cncs';
    }
  }

  private normalizePath(path: string): string {
    const trimmed = `${path ?? ''}`.trim();
    if (!trimmed) {
      return '/';
    }

    const noOrigin = trimmed.replace(/^https?:\/\/[^/]+/i, '');
    const noQueryHash = noOrigin.split('?')[0].split('#')[0];
    return noQueryHash.startsWith('/') ? noQueryHash : `/${noQueryHash}`;
  }
}
